import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const TaxDeductionsCalculator = () => {
  const content = article.find((item) => item.id === 9);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    // Basic Information
    grossIncome: "100000",
    filingStatus: "single",
    state: "CA",
    
    // Standard vs Itemized
    useItemized: "standard",
    
    // Itemized Deductions
    stateLocalTaxes: "10000",
    propertyTax: "6000",
    mortgageInterest: "12000",
    medicalExpenses: "5000",
    charitableCash: "2000",
    charitableNonCash: "1000",
    
    // Above-the-Line Deductions
    retirementContributions: "6000",
    hsaContributions: "3650",
    studentLoanInterest: "2500",
    educatorExpenses: "250",
    selfEmploymentTax: "0",
    
    // Business & Investment Deductions
    homeOfficeExpenses: "1500",
    investmentExpenses: "500",
    rentalExpenses: "0",
    businessExpenses: "0",
    
    // Credits
    childTaxCredit: "2000",
    educationCredits: "2500",
    earnedIncomeCredit: "0",
    solarCredit: "0",
    evCredit: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateDeductions();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateDeductions = () => {
    const {
      grossIncome,
      filingStatus,
      useItemized,
      stateLocalTaxes,
      propertyTax,
      mortgageInterest,
      medicalExpenses,
      charitableCash,
      charitableNonCash,
      retirementContributions,
      hsaContributions,
      studentLoanInterest,
      educatorExpenses,
      selfEmploymentTax,
      homeOfficeExpenses,
      investmentExpenses,
      rentalExpenses,
      businessExpenses,
      childTaxCredit,
      educationCredits,
      earnedIncomeCredit,
      solarCredit,
      evCredit
    } = inputs;

    // Parse inputs
    const gross = parseFloat(grossIncome) || 0;
    const stateLocal = parseFloat(stateLocalTaxes) || 0;
    const property = parseFloat(propertyTax) || 0;
    const mortgage = parseFloat(mortgageInterest) || 0;
    const medical = parseFloat(medicalExpenses) || 0;
    const charityCash = parseFloat(charitableCash) || 0;
    const charityNonCash = parseFloat(charitableNonCash) || 0;
    const retirement = parseFloat(retirementContributions) || 0;
    const hsa = parseFloat(hsaContributions) || 0;
    const studentLoan = parseFloat(studentLoanInterest) || 0;
    const educator = parseFloat(educatorExpenses) || 0;
    const selfEmployment = parseFloat(selfEmploymentTax) || 0;
    const homeOffice = parseFloat(homeOfficeExpenses) || 0;
    const investment = parseFloat(investmentExpenses) || 0;
    const rental = parseFloat(rentalExpenses) || 0;
    const business = parseFloat(businessExpenses) || 0;

    // Standard deduction amounts for 2023
    const standardDeductions = {
      single: 13850,
      married: 27700,
      head: 20800
    };

    const standardDeduction = standardDeductions[filingStatus] || standardDeductions.single;

    // Calculate Above-the-Line Deductions (adjusts AGI)
    const aboveTheLineDeductions = retirement + hsa + studentLoan + educator + selfEmployment;
    const adjustedGrossIncome = Math.max(0, gross - aboveTheLineDeductions);

    // Calculate Itemized Deductions
    // SALT Cap (State and Local Taxes)
    const saltDeduction = Math.min(stateLocal + property, 10000);
    
    // Medical expenses (only amount over 7.5% of AGI)
    const medicalThreshold = adjustedGrossIncome * 0.075;
    const medicalDeduction = Math.max(0, medical - medicalThreshold);
    
    // Charitable contributions
    const charitableDeduction = charityCash + charityNonCash;
    
    // Total itemized deductions
    const totalItemized = saltDeduction + mortgage + medicalDeduction + charitableDeduction;

    // Determine which deduction to use
    const useItemizedDeduction = useItemized === "itemized" && totalItemized > standardDeduction;
    const totalDeduction = useItemizedDeduction ? totalItemized : standardDeduction;

    // Calculate taxable income
    const taxableIncome = Math.max(0, adjustedGrossIncome - totalDeduction);

    // Business & investment deductions (miscellaneous)
    const businessInvestmentDeductions = homeOffice + investment + rental + business;

    // Total credits
    const childCredit = parseFloat(childTaxCredit) || 0;
    const educationCredit = parseFloat(educationCredits) || 0;
    const earnedIncome = parseFloat(earnedIncomeCredit) || 0;
    const solar = parseFloat(solarCredit) || 0;
    const ev = parseFloat(evCredit) || 0;
    const totalCredits = childCredit + educationCredit + earnedIncome + solar + ev;

    // Calculate tax savings (simplified)
    const marginalRate = 0.24; // Assume 24% marginal rate for savings calculation
    const deductionSavings = totalDeduction * marginalRate;
    const aboveTheLineSavings = aboveTheLineDeductions * marginalRate;
    const totalTaxSavings = deductionSavings + aboveTheLineSavings + totalCredits;

    // Effective tax rates
    const taxBeforeDeductions = gross * 0.25; // Simplified
    const taxAfterDeductions = Math.max(0, taxBeforeDeductions - totalTaxSavings);
    const effectiveRate = (taxAfterDeductions / gross) * 100;
    const savingsRate = (totalTaxSavings / gross) * 100;

    const resultData = {
      // Income & Deduction Summary
      grossIncome: gross,
      adjustedGrossIncome: adjustedGrossIncome,
      taxableIncome: taxableIncome,
      
      // Deduction Breakdown
      standardDeduction: standardDeduction,
      totalItemized: totalItemized,
      usedDeduction: useItemizedDeduction ? "Itemized" : "Standard",
      totalDeduction: totalDeduction,
      aboveTheLineDeductions: aboveTheLineDeductions,
      businessInvestmentDeductions: businessInvestmentDeductions,
      
      // Itemized Components
      saltDeduction: saltDeduction,
      mortgageDeduction: mortgage,
      medicalDeduction: medicalDeduction,
      charitableDeduction: charitableDeduction,
      
      // Tax Impact
      deductionSavings: deductionSavings,
      aboveTheLineSavings: aboveTheLineSavings,
      totalCredits: totalCredits,
      totalTaxSavings: totalTaxSavings,
      
      // Rates & Metrics
      effectiveTaxRate: effectiveRate,
      taxSavingsRate: savingsRate,
      deductionToIncomeRatio: (totalDeduction / gross) * 100,
      
      // Recommendations
      shouldItemize: totalItemized > standardDeduction,
      itemizedBenefit: Math.max(0, totalItemized - standardDeduction),
      potentialAdditionalSavings: 0 // Will be calculated
    };

    setResult(resultData);
  };

  const calculateOptimization = () => {
    if (!result) return null;

    const optimizations = [];
    
    // Retirement contribution optimization
    const currentRetirement = parseFloat(inputs.retirementContributions) || 0;
    const maxRetirement = inputs.filingStatus === "single" ? 22500 : 22500;
    if (currentRetirement < maxRetirement) {
      const additionalSavings = (maxRetirement - currentRetirement) * 0.24;
      optimizations.push({
        type: "retirement",
        action: `Increase 401(k) contributions to $${maxRetirement.toLocaleString()}`,
        savings: additionalSavings,
        priority: "high"
      });
    }

    // HSA optimization
    const currentHSA = parseFloat(inputs.hsaContributions) || 0;
    const maxHSA = inputs.filingStatus === "single" ? 3850 : 7750;
    if (currentHSA < maxHSA) {
      const additionalSavings = (maxHSA - currentHSA) * 0.24;
      optimizations.push({
        type: "hsa",
        action: `Max out HSA contributions to $${maxHSA.toLocaleString()}`,
        savings: additionalSavings,
        priority: "high"
      });
    }

    // Charitable giving optimization
    const currentCharity = (parseFloat(inputs.charitableCash) || 0) + (parseFloat(inputs.charitableNonCash) || 0);
    if (currentCharity < 1000 && result.shouldItemize) {
      optimizations.push({
        type: "charity",
        action: "Consider bunching charitable donations",
        savings: 240, // $1000 * 24%
        priority: "medium"
      });
    }

    return optimizations;
  };

  const resetCalculator = () => {
    setInputs({
      grossIncome: "100000",
      filingStatus: "single",
      state: "CA",
      useItemized: "standard",
      stateLocalTaxes: "10000",
      propertyTax: "6000",
      mortgageInterest: "12000",
      medicalExpenses: "5000",
      charitableCash: "2000",
      charitableNonCash: "1000",
      retirementContributions: "6000",
      hsaContributions: "3650",
      studentLoanInterest: "2500",
      educatorExpenses: "250",
      selfEmploymentTax: "0",
      homeOfficeExpenses: "1500",
      investmentExpenses: "500",
      rentalExpenses: "0",
      businessExpenses: "0",
      childTaxCredit: "2000",
      educationCredits: "2500",
      earnedIncomeCredit: "0",
      solarCredit: "0",
      evCredit: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Deduction Category,Amount,Savings",
      `Gross Income,${result.grossIncome.toFixed(2)},`,
      `Adjusted Gross Income,${result.adjustedGrossIncome.toFixed(2)},`,
      `Taxable Income,${result.taxableIncome.toFixed(2)},`,
      `Standard Deduction,${result.standardDeduction.toFixed(2)},`,
      `Total Itemized,${result.totalItemized.toFixed(2)},`,
      `Deduction Used,${result.usedDeduction},`,
      `Above-the-Line Deductions,${result.aboveTheLineDeductions.toFixed(2)},${result.aboveTheLineSavings.toFixed(2)}`,
      `Itemized Deductions,${result.totalDeduction.toFixed(2)},${result.deductionSavings.toFixed(2)}`,
      `Tax Credits,,${result.totalCredits.toFixed(2)}`,
      `Total Tax Savings,,${result.totalTaxSavings.toFixed(2)}`,
      `Effective Tax Rate,,${result.effectiveTaxRate.toFixed(2)}%`,
      `Tax Savings Rate,,${result.taxSavingsRate.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tax-deductions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Deductions data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Deductions calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Deductions calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      homeowner: { 
        useItemized: "itemized",
        propertyTax: "8000",
        mortgageInterest: "15000",
        stateLocalTaxes: "12000"
      },
      investor: {
        investmentExpenses: "2000",
        retirementContributions: "19500",
        charitableCash: "5000"
      },
      business: {
        selfEmploymentTax: "5000",
        homeOfficeExpenses: "3000",
        businessExpenses: "8000"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} deductions preset applied`, "info");
  };

  // Chart Data
  const deductionsChartData = result ? {
    labels: ["Above-the-Line", "Itemized", "Business/Investment", "Tax Credits"],
    datasets: [{
      data: [
        result.aboveTheLineDeductions,
        result.totalDeduction,
        result.businessInvestmentDeductions,
        result.totalCredits
      ],
      backgroundColor: ["#19692C", "#28A745", "#2B8C3A", "#FFD93D"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const itemizedBreakdownData = result ? {
    labels: ["SALT Taxes", "Mortgage Interest", "Charitable", "Medical"],
    datasets: [{
      label: 'Itemized Deductions',
      data: [
        result.saltDeduction,
        result.mortgageDeduction,
        result.charitableDeduction,
        result.medicalDeduction
      ],
      backgroundColor: ["#19692C", "#28A745", "#2B8C3A", "#1A7C2A"],
      borderWidth: 2
    }]
  } : null;

  const savingsChartData = result ? {
    labels: ["Deduction Savings", "Above-the-Line Savings", "Tax Credits"],
    datasets: [{
      label: 'Tax Savings',
      data: [
        result.deductionSavings,
        result.aboveTheLineSavings,
        result.totalCredits
      ],
      backgroundColor: ["#19692C", "#28A745", "#FFD93D"],
      borderWidth: 2
    }]
  } : null;

  const optimizations = calculateOptimization();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Tax Deductions Calculator</h1>
        </div>
        <p>Maximize your tax savings with comprehensive deductions analysis and optimization strategies</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Deductions Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        ${calc.result.totalTaxSavings.toFixed(0)} Saved
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Deductions Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-magic"></i>
                {isMobile ? 'Optimize' : 'Tax Optimization'}
              </button>
              <button 
                className={`tab ${activeTab === "strategies" ? "active" : ""}`}
                onClick={() => setActiveTab("strategies")}
              >
                <i className="fas fa-chess"></i>
                {isMobile ? 'Strategies' : 'Tax Strategies'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Common Scenarios:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('homeowner')}>
                Homeowner
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('investor')}>
                Investor
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('business')}>
                Business Owner
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-user"></i>
                    Basic Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Gross Income</label>
                      <input 
                        type="number" 
                        name="grossIncome" 
                        value={inputs.grossIncome} 
                        onChange={handleChange}
                        placeholder="100000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-users"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="head">Head of Household</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-toggle-on"></i> Deduction Method</label>
                      <select name="useItemized" value={inputs.useItemized} onChange={handleChange}>
                        <option value="standard">Standard Deduction</option>
                        <option value="itemized">Itemized Deductions</option>
                        <option value="auto">Auto-Select Best</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-home"></i>
                    Itemized Deductions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-building"></i> State & Local Taxes</label>
                      <input 
                        type="number" 
                        name="stateLocalTaxes" 
                        value={inputs.stateLocalTaxes} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-house"></i> Property Tax</label>
                      <input 
                        type="number" 
                        name="propertyTax" 
                        value={inputs.propertyTax} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Mortgage Interest</label>
                      <input 
                        type="number" 
                        name="mortgageInterest" 
                        value={inputs.mortgageInterest} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-heartbeat"></i> Medical Expenses</label>
                      <input 
                        type="number" 
                        name="medicalExpenses" 
                        value={inputs.medicalExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-heart"></i> Charitable Cash</label>
                      <input 
                        type="number" 
                        name="charitableCash" 
                        value={inputs.charitableCash} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-gift"></i> Charitable Non-Cash</label>
                      <input 
                        type="number" 
                        name="charitableNonCash" 
                        value={inputs.charitableNonCash} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-piggy-bank"></i>
                    Above-the-Line Deductions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-retirement"></i> Retirement Contributions</label>
                      <input 
                        type="number" 
                        name="retirementContributions" 
                        value={inputs.retirementContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-heartbeat"></i> HSA Contributions</label>
                      <input 
                        type="number" 
                        name="hsaContributions" 
                        value={inputs.hsaContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-graduation-cap"></i> Student Loan Interest</label>
                      <input 
                        type="number" 
                        name="studentLoanInterest" 
                        value={inputs.studentLoanInterest} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chalkboard-teacher"></i> Educator Expenses</label>
                      <input 
                        type="number" 
                        name="educatorExpenses" 
                        value={inputs.educatorExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-briefcase"></i>
                    Business & Investment
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-laptop-house"></i> Home Office Expenses</label>
                      <input 
                        type="number" 
                        name="homeOfficeExpenses" 
                        value={inputs.homeOfficeExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Investment Expenses</label>
                      <input 
                        type="number" 
                        name="investmentExpenses" 
                        value={inputs.investmentExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-building"></i> Rental Property Expenses</label>
                      <input 
                        type="number" 
                        name="rentalExpenses" 
                        value={inputs.rentalExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-briefcase"></i> Business Expenses</label>
                      <input 
                        type="number" 
                        name="businessExpenses" 
                        value={inputs.businessExpenses} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-award"></i>
                    Tax Credits
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-child"></i> Child Tax Credit</label>
                      <input 
                        type="number" 
                        name="childTaxCredit" 
                        value={inputs.childTaxCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-graduation-cap"></i> Education Credits</label>
                      <input 
                        type="number" 
                        name="educationCredits" 
                        value={inputs.educationCredits} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-sun"></i> Solar Energy Credit</label>
                      <input 
                        type="number" 
                        name="solarCredit" 
                        value={inputs.solarCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-car"></i> Electric Vehicle Credit</label>
                      <input 
                        type="number" 
                        name="evCredit" 
                        value={inputs.evCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Optimization Tab */}
          {activeTab === "optimization" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-magic"></i>
                  Tax Optimization Opportunities
                </h3>
                {optimizations && optimizations.length > 0 ? (
                  <div className="scenario-list">
                    {optimizations.map((opt, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-name">
                          <i className={`fas fa-${opt.priority === 'high' ? 'exclamation-triangle' : 'lightbulb'}`}></i>
                          {opt.action}
                        </div>
                        <div className="scenario-outcome">
                          Potential savings: ${opt.savings.toFixed(0)}
                        </div>
                        <div className="scenario-stats">
                          <span className={`priority ${opt.priority}`}>
                            {opt.priority.toUpperCase()} PRIORITY
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <i className="fas fa-check-circle fa-2x"></i>
                    <p>Great job! You're already maximizing your tax deductions.</p>
                    <small>Enter your financial details in the calculator tab to see optimization opportunities.</small>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Strategies Tab */}
          {activeTab === "strategies" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-chess"></i>
                Advanced Tax Strategies
              </h3>
              <div className="strategy-list">
                <div className="strategy-item">
                  <h4>Bunching Strategy</h4>
                  <p>Combine multiple years of charitable contributions into one year to exceed the standard deduction threshold.</p>
                  <div className="strategy-benefit">Benefit: Enables itemizing in alternating years</div>
                </div>
                <div className="strategy-item">
                  <h4>Tax-Loss Harvesting</h4>
                  <p>Sell investments at a loss to offset capital gains and up to $3,000 of ordinary income.</p>
                  <div className="strategy-benefit">Benefit: Reduces taxable income immediately</div>
                </div>
                <div className="strategy-item">
                  <h4>Retirement Stacking</h4>
                  <p>Maximize 401(k), IRA, and HSA contributions for triple tax benefits.</p>
                  <div className="strategy-benefit">Benefit: Tax deduction + tax-free growth + tax-free withdrawals</div>
                </div>
                <div className="strategy-item">
                  <h4>Business Expense Optimization</h4>
                  <p>Properly track and deduct home office, vehicle, and equipment expenses.</p>
                  <div className="strategy-benefit">Benefit: Reduces self-employment tax burden</div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Deductions Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No deductions calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-piggy-bank"></i>
                          ${calc.result.totalTaxSavings.toFixed(0)} Saved
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Savings Rate: {calc.result.taxSavingsRate.toFixed(1)}%</span>
                        <span><i className="fas fa-toggle-on"></i> Method: {calc.result.usedDeduction}</span>
                        <span><i className="fas fa-dollar-sign"></i> AGI: ${calc.result.adjustedGrossIncome.toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "optimization") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-piggy-bank"></i>
                    Deductions Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.totalTaxSavings.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-piggy-bank"></i>
                          Total Tax Savings
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.taxSavingsRate.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Savings Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.effectiveTaxRate.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Effective Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.usedDeduction}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-toggle-on"></i>
                          Deduction Method
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Gross Income:</span>
                        <span>${result.grossIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Adjusted Gross Income:</span>
                        <span>${result.adjustedGrossIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Total Deductions:</span>
                        <span>${result.totalDeduction.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-file-invoice"></i> Taxable Income:</span>
                        <span>${result.taxableIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-up"></i> Above-the-Line Deductions:</span>
                        <span>${result.aboveTheLineDeductions.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Tax Credits:</span>
                        <span>${result.totalCredits.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Recommendation */}
                    {result.shouldItemize && inputs.useItemized !== "itemized" && (
                      <div className="risk-summary">
                        <h4>
                          <i className="fas fa-lightbulb"></i>
                          Optimization Opportunity
                        </h4>
                        <div className="recommendation">
                          <p>You could save an additional <strong>${result.itemizedBenefit.toFixed(0)}</strong> by switching to itemized deductions!</p>
                          <br/><button 
                            className="btn save-btn" 
                            onClick={() => {
                              setInputs(prev => ({...prev, useItemized: "itemized"}));
                              addNotification("Switched to itemized deductions", "success");
                            }}
                          >
                            <i className="fas fa-toggle-on"></i>
                            Switch to Itemized
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Deductions Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Deductions Breakdown
                      </h3>
                      <Doughnut 
                        data={deductionsChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Savings Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Tax Savings Breakdown
                      </h3>
                      <Bar 
                        data={savingsChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Itemized Breakdown (if applicable) */}
                    {result.usedDeduction === "Itemized" && (
                      <div className="chart-container">
                        <h3>
                          <i className="fas fa-chart-bar"></i>
                          Itemized Deductions Breakdown
                        </h3>
                        <Bar 
                          data={itemizedBreakdownData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                display: false
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: function(value) {
                                    return '$' + value.toLocaleString();
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your deduction details to see tax savings</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "optimization" && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-trophy"></i>
                  Your Tax Efficiency Score
                </h3>
                {result ? (
                  <>
                    <div className="efficiency-score">
                      <div className="score-circle">
                        <div className="score-value">{Math.min(100, Math.max(60, 100 - result.effectiveTaxRate)).toFixed(0)}</div>
                        <div className="score-label">Efficiency Score</div>
                      </div>
                      <div className="score-breakdown">
                        <div className="score-metric">
                          <span>Deduction Utilization</span>
                          <div className="metric-bar">
                            <div 
                              className="metric-fill" 
                              style={{ width: `${Math.min(100, (result.totalDeduction / result.grossIncome) * 200)}%` }}
                            ></div>
                          </div>
                          <span>{(result.deductionToIncomeRatio).toFixed(1)}%</span>
                        </div>
                        <div className="score-metric">
                          <span>Credit Optimization</span>
                          <div className="metric-bar">
                            <div 
                              className="metric-fill" 
                              style={{ width: `${Math.min(100, (result.totalCredits / result.grossIncome) * 1000)}%` }}
                            ></div>
                          </div>
                          <span>${result.totalCredits.toFixed(0)}</span>
                        </div>
                        <div className="score-metric">
                          <span>Tax Savings Rate</span>
                          <div className="metric-bar">
                            <div 
                              className="metric-fill" 
                              style={{ width: `${result.taxSavingsRate}%` }}
                            ></div>
                          </div>
                          <span>{result.taxSavingsRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>

                    <div className="optimization-tips">
                      <h4>
                        <i className="fas fa-bullseye"></i>
                        Quick Wins
                      </h4>
                      <div className="tips-list">
                        <div className="tip-item">
                          <i className="fas fa-retirement"></i>
                          <span>Maximize retirement contributions to reduce AGI</span>
                        </div>
                        <div className="tip-item">
                          <i className="fas fa-heartbeat"></i>
                          <span>Consider HSA for triple tax benefits</span>
                        </div>
                        <div className="tip-item">
                          <i className="fas fa-hand-holding-heart"></i>
                          <span>Bundle charitable donations for better deduction impact</span>
                        </div>
                        {result.usedDeduction === "Standard" && result.shouldItemize && (
                          <div className="tip-item highlight">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>Switch to itemized deductions for additional savings!</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-chart-line fa-3x"></i>
                    <p>Complete the calculator to see optimization opportunities</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default TaxDeductionsCalculator;